// Shared audit history panel + per-question history tooltips for form workflows.

export function shouldShowAuditHistory(stackStatus) {
  return ["completed", "editing"].includes(stackStatus);
}

export function buildAuditHistoryJsonUrl(path) {
  if (!path) return null;

  const absolute = path.startsWith("http")
    ? path
    : `${document.location.origin}${path.startsWith("/") ? "" : "/"}${path}`;

  const url = new URL(absolute);
  if (!url.pathname.endsWith(".json")) {
    url.pathname = `${url.pathname.replace(/\/$/, "")}.json`;
  }

  return url.toString();
}

export async function fetchAuditHistory(auditHistoryUrl) {
  if (!auditHistoryUrl) return null;

  const response = await fetch(buildAuditHistoryJsonUrl(auditHistoryUrl), {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) return null;

  return response.json();
}

export function formatAuditTimestamp(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes} UTC`;
}

function isInitialSubmission(version) {
  if (version.initial_submission != null) {
    return version.initial_submission;
  }

  return ["Initial Submission", "Original Submission"].includes(version.action);
}

export function hasEditHistory(historyData) {
  if (historyData?.has_edit_history != null) {
    return historyData.has_edit_history;
  }

  const summary = historyData?.change_summary;
  if (
    summary?.has_changes &&
    resolveChangeSummaryRevisions(summary).length > 0
  ) {
    return true;
  }

  const versions = historyData?.versions || [];
  return versions.some((version) => !isInitialSubmission(version));
}

export function resolveAuditHistoryVersions(historyData) {
  const versions = historyData?.versions || [];
  if (!versions.length) return [];
  if (hasEditHistory(historyData)) return versions;

  return [versions[0]];
}

function buildAuditHistorySignatureHtml(
  version,
  {
    print = false,
    origin = window.location.origin,
    framed = true,
    compact = false,
  } = {},
) {
  const signatureSrc = version.signature_url
    ? print && !version.signature_url.startsWith("http")
      ? `${origin}${version.signature_url}`
      : version.signature_url
    : null;

  const maxHeight = compact ? "48px" : "64px";

  if (signatureSrc) {
    const imageHtml = `<img src="${signatureSrc}" alt="Electronic Signature" class="audit-history-signature" style="max-height: ${maxHeight}; width: auto; height: auto; display: block;">`;

    if (!framed) return imageHtml;

    if (print) {
      return `
        <div style="padding: ${compact ? "4px 0" : "8px 10px"}; background: #fff; ${compact ? "" : "border: 1px dashed #cbd5e1; border-radius: 6px;"} display: flex; align-items: center;">
          ${imageHtml}
        </div>
      `;
    }

    return `
      <div class="${compact ? "" : "p-2 bg-white border border-dashed border-gray-300 rounded"} flex items-center">
        ${imageHtml}
      </div>
    `;
  }

  const emptyMessage = "No signature on file";

  if (print) {
    return `<span style="font-size: 12px; color: #6b7280; font-style: italic;">${emptyMessage}</span>`;
  }

  return `<span class="text-xs text-gray-500 italic">${emptyMessage}</span>`;
}

function buildAuditHistoryVersionMeta(version) {
  const isInitial = isInitialSubmission(version);
  const actorLabel = isInitial ? "Submitted By" : "Modified By";
  const timestamp = formatAuditTimestamp(version.submitted_at);
  const action =
    version.action || (isInitial ? "Initial Submission" : "Edited Submission");
  const signatureMeaning =
    version.signature_meaning ||
    (isInitial
      ? "I have reviewed the information contained in this record and attest that it is complete, accurate, and reflective of my professional assessment. By applying my electronic signature, I accept responsibility for this entry."
      : "I have reviewed the revised information contained in this record and attest that the changes are complete, accurate, and reflective of my professional assessment. By applying my electronic signature, I acknowledge and approve these modifications and accept responsibility for the updated record.");
  const isLegacy = action === "Legacy Submission";

  return {
    isInitial,
    title: isLegacy
      ? "Legacy Submission"
      : isInitial
        ? "Initial Submission"
        : "Edited Submission",
    versionLabel: isLegacy
      ? "Legacy"
      : isInitial
        ? "Initial"
        : `V${version.version_number}`,
    action,
    actorLabel,
    actorName: version.user_name || "—",
    role: version.user_role || "—",
    sponsor: version.sponsor || version.certificate_details?.sponsor || "—",
    timestamp,
    signatureMeaning,
    signatureUniqueId: version.signature_unique_id || "—",
    certificateDetails: version.certificate_details || null,
  };
}

function renderAuditHistoryCertificatePanel(
  details,
  { print = false, hidden = true } = {},
) {
  if (!details) return "";

  const studyLines = [
    ["Study", details.study_name],
    ["Study Short Name", details.study_short_name],
    ["Study Code", details.study_code],
    ["Sponsor", details.sponsor],
    ["Protocol Number", details.protocol_number],
  ];

  const documentLines = [
    ["QC Form ID", details.qc_form_id],
    ["Form Version", details.form_version],
    ["Subject ID", details.subject_id],
    ["Visit", details.visit_name],
    ["Modality", details.modality_name],
    ["Form Status", details.form_status],
    ["Date Generated", formatAuditTimestamp(details.generated_at)],
  ];

  const signerLines = [
    ["Email Address", details.user_email],
    ["Authentication Method", details.authentication_method],
    ["IP Address", details.ip_address],
  ];

  const renderSection = (title, lines) => {
    const rows = lines
      .map(([label, value]) =>
        renderAuditHistoryCompactLine(label, value || "—", { print }),
      )
      .join("");

    if (print) {
      return `
        <div style="margin-bottom: 8px;">
          <div style="font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #64748b; margin-bottom: 4px;">${title}</div>
          ${rows}
        </div>
      `;
    }

    return `
      <div class="mb-2">
        <div class="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">${title}</div>
        ${rows}
      </div>
    `;
  };

  if (print) {
    return `
      <div style="margin-top: 8px; padding: 8px 10px; background: #f8fafc; border: 1px solid #dbeafe; border-radius: 6px;">
        <div style="font-size: 11px; font-weight: 700; color: #0369a1; margin-bottom: 6px;">Signature Certificate Details</div>
        ${renderSection("Study Information", studyLines)}
        ${renderSection("Document Information", documentLines)}
        ${renderSection("Signer Verification", signerLines)}
      </div>
    `;
  }

  const hiddenClass = hidden ? "hidden" : "";

  return `
    <div class="audit-history-certificate-panel ${hiddenClass} mt-2 p-2 bg-blue-50 border border-blue-100 rounded">
      <div class="text-xs font-bold text-blue-800 mb-2">Signature Certificate Details</div>
      ${renderSection("Study Information", studyLines)}
      ${renderSection("Document Information", documentLines)}
      ${renderSection("Signer Verification", signerLines)}
    </div>
  `;
}

function renderAuditHistoryUniqueId(
  uniqueId,
  { print = false, certificateDetails = null } = {},
) {
  const idValue = escapeHtml(uniqueId);
  const certificateHtml = certificateDetails
    ? renderAuditHistoryCertificatePanel(certificateDetails, {
        print,
        hidden: !print,
      })
    : "";

  if (print) {
    return `
      <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #64748b; word-break: break-all;">
        <span style="font-weight: 600;">Unique ID:</span>
        <span style="font-family: 'Courier New', Courier, monospace; letter-spacing: 0.03em;">${idValue}</span>
        ${certificateHtml}
      </div>
    `;
  }

  return `
    <div class="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500" style="word-break: break-all;">
      <span class="font-semibold">Unique ID:</span>
      <button
        type="button"
        class="audit-history-unique-id-toggle text-blue-700 hover:text-blue-900 focus:outline-none"
        style="font-family: 'Courier New', Courier, monospace; letter-spacing: 0.03em; text-decoration: underline; text-decoration-style: dotted;"
        aria-expanded="false"
        title="Show signature certificate details"
      >
        ${idValue}
      </button>
      <span class="text-gray-400 italic ml-1">(click for details)</span>
      ${certificateHtml}
    </div>
  `;
}

function renderAuditHistoryCompactLine(label, value, { print = false } = {}) {
  if (print) {
    return `<div style="font-size: 12px; line-height: 1.45; color: #374151; margin-bottom: 1px;"><span style="font-weight: 600;">${label}:</span> ${escapeHtml(value)}</div>`;
  }

  return `<div class="text-xs text-gray-700" style="line-height: 1.45; margin-bottom: 1px;"><span class="font-semibold">${label}:</span> ${escapeHtml(value)}</div>`;
}

function renderAuditHistorySignedBlock(
  meta,
  signatureHtml,
  { print = false, accentColor },
) {
  const metaLines = [
    ["Action", meta.action],
    [meta.actorLabel, meta.actorName],
    ["Role", meta.role],
    ["Sponsor", meta.sponsor],
    ["Date & Time", meta.timestamp],
    ["Meaning of Signature", meta.signatureMeaning],
  ]
    .map(([label, value]) =>
      renderAuditHistoryCompactLine(label, value, { print }),
    )
    .join("");

  const uniqueIdHtml = renderAuditHistoryUniqueId(meta.signatureUniqueId, {
    print,
    certificateDetails: meta.certificateDetails,
  });

  if (print) {
    return `
      <div style="display: flex; background: #fff; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden;">
        <div style="width: 3px; background: ${accentColor}; flex-shrink: 0;"></div>
        <div style="padding: 8px 10px; flex: 1;">
          <div style="font-size: 11px; font-weight: 700; color: ${accentColor}; margin-bottom: 4px;">Signed by:</div>
          <div style="margin-bottom: 6px;">${signatureHtml}</div>
          ${metaLines}
          ${uniqueIdHtml}
        </div>
      </div>
    `;
  }

  return `
    <div class="flex bg-white border border-gray-200 rounded overflow-hidden">
      <div style="width: 3px; background: ${accentColor}; flex-shrink: 0;"></div>
      <div class="py-2 px-3 flex-1">
        <div class="text-xs font-bold mb-1" style="color: ${accentColor};">Signed by:</div>
        <div class="mb-2">${signatureHtml}</div>
        ${metaLines}
        ${uniqueIdHtml}
      </div>
    </div>
  `;
}

function renderAuditHistoryVersionContent(
  version,
  { print = false, origin = window.location.origin } = {},
) {
  const meta = buildAuditHistoryVersionMeta(version);
  const accentColor = meta.isInitial ? "#15803d" : "#0369a1";
  const badgeBg = meta.isInitial ? "#dcfce7" : "#e0f2fe";
  const badgeText = meta.isInitial ? "#166534" : "#075985";
  const signatureHtml = buildAuditHistorySignatureHtml(version, {
    print,
    origin,
    compact: true,
  });
  const signedBlock = renderAuditHistorySignedBlock(meta, signatureHtml, {
    print,
    accentColor,
  });

  if (print) {
    return `
      <div style="border: 1px solid #dbeafe; border-left: 4px solid ${accentColor}; border-radius: 8px; overflow: hidden; margin-bottom: 10px; page-break-inside: avoid; background: #f8fafc;">
        <div style="display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fff; border-bottom: 1px solid #e5e7eb;">
          <span style="display: inline-flex; align-items: center; justify-content: center; min-width: 36px; padding: 2px 8px; border-radius: 9999px; background: ${badgeBg}; color: ${badgeText}; font-size: 11px; font-weight: 700;">${escapeHtml(meta.versionLabel)}</span>
          <span style="font-size: 14px; font-weight: 700; color: #111827;">${escapeHtml(meta.title)}</span>
        </div>
        <div style="padding: 10px 12px;">
          ${signedBlock}
        </div>
      </div>
    `;
  }

  return `
    <div class="audit-history-version border border-blue-100 rounded-lg overflow-hidden shadow-sm mb-2" style="border-left: 4px solid ${accentColor}; background: #f8fafc;">
      <div class="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
        <span class="inline-flex items-center justify-center px-2 py-0 rounded-full text-xs font-bold" style="background: ${badgeBg}; color: ${badgeText}; min-width: 36px;">
          ${escapeHtml(meta.versionLabel)}
        </span>
        <span class="text-sm font-bold text-gray-900">${escapeHtml(meta.title)}</span>
      </div>
      <div class="p-2">
        ${signedBlock}
      </div>
    </div>
  `;
}

export function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Server-formatted field values may include trusted badge HTML from format_field_value.
function formatAuditDisplayValue(value, { allowHtml = false } = {}) {
  if (value == null || value === "") return "—";

  const stringValue = String(value);
  if (!allowHtml) return escapeHtml(stringValue);

  return stringValue;
}

function normalizeAuditDisplayForCompare(value) {
  if (value == null) return null;

  let str = String(value).trim();
  if (str.includes("<")) {
    str = str.replace(/<[^>]+>/g, "").trim();
  }
  if (!str || str === "—" || str === "-") return null;

  return str;
}

function equivalentAuditDisplayValues(oldValue, newValue) {
  return (
    normalizeAuditDisplayForCompare(oldValue) ===
    normalizeAuditDisplayForCompare(newValue)
  );
}

export function buildAuditHistoryVersionHtml(
  version,
  { print = false, origin = window.location.origin } = {},
) {
  return renderAuditHistoryVersionContent(version, { print, origin });
}

export function buildAuditHistoryPrintHtml(
  historyData,
  origin = window.location.origin,
) {
  const versions = resolveAuditHistoryVersions(historyData);
  const message = historyData?.message;

  if (!versions.length && !message) return "";

  const versionsHtml = versions
    .map((version) =>
      buildAuditHistoryVersionHtml(version, { print: true, origin }),
    )
    .join("");

  const noticeHtml = message
    ? `<div style="margin-bottom: 12px; padding: 8px 10px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 12px; color: #92400e;">${escapeHtml(message)}</div>`
    : "";

  const emptyHtml = !versions.length
    ? `<p style="font-size: 13px; color: #6b7280; font-style: italic; margin: 0;">No audit history versions are available for this form.</p>`
    : "";

  return `
    <div class="audit-history-print" style="margin-top: 24px; font-family: Calibri, sans-serif;">
      <div style="background: #17a2b8; color: #fff; padding: 10px 16px; font-weight: bold; font-size: 16px; border-radius: 8px 8px 0 0;">
        Audit History
      </div>
      <div style="padding: 16px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 8px 8px;">
        ${noticeHtml}
        ${versionsHtml || emptyHtml}
      </div>
    </div>
  `;
}

function statusPillHtml(label, { print = false, tone = "neutral" } = {}) {
  const styles = {
    editing: { bg: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
    completed: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" },
    neutral: { bg: "#e5e7eb", color: "#374151", border: "#d1d5db" },
  };
  const toneKey = String(label || "")
    .toLowerCase()
    .includes("edit")
    ? "editing"
    : String(label || "")
          .toLowerCase()
          .includes("complete")
      ? "completed"
      : "neutral";
  const palette = styles[tone] || styles[toneKey] || styles.neutral;
  const text = escapeHtml(label || "—");

  if (print) {
    return `<span style="display: inline-block; padding: 2px 8px; border-radius: 9999px; background: ${palette.bg}; color: ${palette.color}; border: 1px solid ${palette.border}; font-size: 11px; font-weight: 700;">${text}</span>`;
  }

  return `<span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold" style="background: ${palette.bg}; color: ${palette.color}; border: 1px solid ${palette.border};">${text}</span>`;
}

export function resolveChangeSummaryRevisions(summary) {
  if (!summary) return [];

  if (Array.isArray(summary.revisions) && summary.revisions.length) {
    return summary.revisions.filter((revision) =>
      (revision.changes || []).some(
        (change) =>
          !equivalentAuditDisplayValues(change.old_value, change.new_value),
      ),
    );
  }

  if (summary.has_changes && summary.changes?.length) {
    return [summary];
  }

  return [];
}

const QUERY_AUDIT_FIELD_KEYS = new Set([
  "query_mgmt",
  "query_title",
  "query_status",
  "query_category",
  "query_comments",
]);

function changeSupportsReasonForChange(change) {
  if (change?.supports_reason_for_change != null) {
    return change.supports_reason_for_change;
  }

  return !QUERY_AUDIT_FIELD_KEYS.has(change?.field_key);
}

function revisionShowsReasonForChange(revision) {
  if (revision?.show_reason_for_change != null) {
    return revision.show_reason_for_change;
  }

  return (revision?.changes || []).some((change) =>
    changeSupportsReasonForChange(change),
  );
}

function buildChangeSummaryRowsHtml(
  changes,
  { print = false, showReasonForChange = true } = {},
) {
  return (changes || [])
    .map((change) => {
      const reasonCellHtml = showReasonForChange
        ? print
          ? `<td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px;">${changeSupportsReasonForChange(change) ? formatAuditDisplayValue(change.reason_for_change, { allowHtml: true }) : ""}</td>`
          : `<td class="border border-gray-300 px-2 py-1 text-sm">${changeSupportsReasonForChange(change) ? formatAuditDisplayValue(change.reason_for_change, { allowHtml: true }) : ""}</td>`
        : "";

      if (print) {
        return `
        <tr>
          <td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px;">${escapeHtml(change.field_name || change.field_key || "—")}</td>
          <td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; color: #b91c1c;">${formatAuditDisplayValue(change.old_value, { allowHtml: true })}</td>
          <td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px; color: #15803d;">${formatAuditDisplayValue(change.new_value, { allowHtml: true })}</td>
          <td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px;">${escapeHtml(change.changed_by || "—")}</td>
          <td style="border: 1px solid #d1d5db; padding: 6px 8px; font-size: 12px;">${escapeHtml(formatAuditTimestamp(change.changed_on))}</td>
          ${reasonCellHtml}
        </tr>
      `;
      }

      return `
      <tr class="bg-white">
        <td class="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">${escapeHtml(change.field_name || change.field_key || "—")}</td>
        <td class="border border-gray-300 px-2 py-1 text-sm text-red-700 break-words">${formatAuditDisplayValue(change.old_value, { allowHtml: true })}</td>
        <td class="border border-gray-300 px-2 py-1 text-sm text-green-700 break-words">${formatAuditDisplayValue(change.new_value, { allowHtml: true })}</td>
        <td class="border border-gray-300 px-2 py-1 text-sm">${escapeHtml(change.changed_by || "—")}</td>
        <td class="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">${escapeHtml(formatAuditTimestamp(change.changed_on))}</td>
        ${reasonCellHtml}
      </tr>
    `;
    })
    .join("");
}

function buildChangeSummaryTableHtml({
  print = false,
  showReasonForChange = true,
} = {}) {
  const reasonHeaderHtml = showReasonForChange
    ? print
      ? '<th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px;">Reason for Change</th>'
      : '<th class="text-left border border-gray-300 px-2 py-1 font-semibold">Reason for Change</th>'
    : "";

  if (print) {
    return `
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="background: #f3f4f6;">
            <th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px;">Field Name</th>
            <th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px; color: #b91c1c;">Old Value</th>
            <th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px; color: #15803d;">New Value</th>
            <th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px;">Changed By</th>
            <th style="border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; font-size: 12px;">Changed On</th>
            ${reasonHeaderHtml}
          </tr>
        </thead>
        <tbody>
          __ROWS__
        </tbody>
      </table>
    `;
  }

  return `
    <table class="table-auto w-full border border-gray-300 text-sm">
      <thead class="bg-gray-100">
        <tr>
          <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Field Name</th>
          <th class="text-left text-red-700 border border-gray-300 px-2 py-1 font-semibold">Old Value</th>
          <th class="text-left text-green-700 border border-gray-300 px-2 py-1 font-semibold">New Value</th>
          <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Changed By</th>
          <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Changed On</th>
          ${reasonHeaderHtml}
        </tr>
      </thead>
      <tbody>
        __ROWS__
      </tbody>
    </table>
  `;
}

function buildChangeSummaryRevisionMetaHtml(
  revision,
  { print = false, showHeading = false } = {},
) {
  const completedBy = [
    revision.completed_by,
    revision.completed_by_role ? `(${revision.completed_by_role})` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const revisionLabel =
    revision.revision_label ||
    (revision.version_number
      ? `Revision V${revision.version_number}`
      : revision.revision_number
        ? `Revision ${revision.revision_number}`
        : "Revision");

  const headingHtml = showHeading
    ? print
      ? `<div style="font-size: 13px; font-weight: 700; color: #0f172a; margin-bottom: 8px;">${escapeHtml(revisionLabel)}</div>`
      : `<div class="text-sm font-bold text-gray-900 mb-2">${escapeHtml(revisionLabel)}</div>`
    : "";

  const showStatusChange = revision.show_status_change !== false;
  const statusChangeHtml = showStatusChange
    ? print
      ? `<div style="margin-bottom: 4px;">
          <span style="font-weight: 700;">Status Change:</span>
          ${statusPillHtml(revision.from_status, { print: true })}
          <span style="margin: 0 6px;">→</span>
          ${statusPillHtml(revision.to_status, { print: true })}
        </div>`
      : `<div class="flex flex-wrap items-center gap-2 mb-1">
        <span class="font-semibold">Status Change:</span>
        ${statusPillHtml(revision.from_status)}
        <span class="text-gray-400">→</span>
        ${statusPillHtml(revision.to_status)}
      </div>`
    : "";

  if (print) {
    return `
      ${headingHtml}
      <div style="margin-bottom: 12px; padding: 8px 10px; background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 12px; color: #374151;">
        ${statusChangeHtml}
        <div><span style="font-weight: 700;">Completed By:</span> ${escapeHtml(completedBy || "—")}</div>
        <div><span style="font-weight: 700;">Completed On:</span> ${escapeHtml(formatAuditTimestamp(revision.completed_on))}</div>
      </div>
    `;
  }

  return `
    ${headingHtml}
    <div class="mb-3 px-3 py-2 rounded bg-white border border-gray-200 text-sm text-gray-700">
      ${statusChangeHtml}
      <div><span class="font-semibold">Completed By:</span> ${escapeHtml(completedBy || "—")}</div>
      <div><span class="font-semibold">Completed On:</span> ${escapeHtml(formatAuditTimestamp(revision.completed_on))}</div>
    </div>
  `;
}

function buildChangeSummaryRevisionBlockHtml(
  revision,
  { print = false, showHeading = false } = {},
) {
  const changes = (revision.changes || []).filter(
    (change) => !equivalentAuditDisplayValues(change.old_value, change.new_value),
  );
  // Status-only edits (e.g. Completed → Editing) must not appear in Change Summary.
  if (!changes.length) return "";

  let tableSectionHtml = "";
  if (changes.length > 0) {
    const showReasonForChange = false;
    const rowsHtml = buildChangeSummaryRowsHtml(changes, {
      print,
      showReasonForChange,
    });
    const tableHtml = buildChangeSummaryTableHtml({
      print,
      showReasonForChange,
    }).replace("__ROWS__", rowsHtml);

    tableSectionHtml = `
      <div class="${print ? "" : "overflow-x-auto"}">
        ${tableHtml}
      </div>
    `;
  }

  return `
    <div class="${print ? "" : "change-summary-revision mb-4 last:mb-0"}" style="${print ? "margin-bottom: 18px; page-break-inside: avoid;" : ""}">
      ${buildChangeSummaryRevisionMetaHtml(revision, { print, showHeading })}
      ${tableSectionHtml}
    </div>
  `;
}

export function buildChangeSummaryContentHtml(summary, { print = false } = {}) {
  const revisions = resolveChangeSummaryRevisions(summary);
  if (!revisions.length) return "";

  const showHeading = revisions.length > 1;

  return revisions
    .map((revision) =>
      buildChangeSummaryRevisionBlockHtml(revision, { print, showHeading }),
    )
    .join("");
}

export function buildChangeSummaryPrintHtml(historyData) {
  if (!hasEditHistory(historyData)) return "";

  const summary = historyData?.change_summary;
  const contentHtml = buildChangeSummaryContentHtml(summary, { print: true });

  if (!summary?.has_changes || !contentHtml) return "";

  return `
    <div class="change-summary-print" style="margin-top: 24px; font-family: Calibri, sans-serif; page-break-inside: avoid;">
      <div style="background: #17a2b8; color: #fff; padding: 10px 16px; font-weight: bold; font-size: 16px; border-radius: 8px 8px 0 0;">
        Change Summary (Revision History)
      </div>
      <div style="padding: 16px; border: 1px solid #ddd; border-top: none; border-radius: 0 0 8px 8px;">
        ${contentHtml}
        <div style="margin-top: 10px; font-size: 11px; color: #6b7280;">
          All changes are captured as part of the audit trail in compliance with 21 CFR Part 11.
        </div>
      </div>
    </div>
  `;
}

export default class FormAuditHistoryModule {
  constructor({
    form,
    formSchema,
    auditHistoryUrl,
    auditHistoryTarget,
    changeSummaryTarget = null,
    stackStatus,
  }) {
    this.form = form;
    this.formSchema = formSchema;
    this.auditHistoryUrl = auditHistoryUrl;
    this.auditHistoryTarget = auditHistoryTarget;
    this.changeSummaryTarget = changeSummaryTarget;
    this.stackStatus = stackStatus;
    this.historyData = null;
  }

  async load() {
    if (!this.auditHistoryUrl || !this.shouldShowPanel()) {
      this.hidePanel();
      this.hideChangeSummary();
      return;
    }

    try {
      const response = await fetch(this.auditHistoryUrl, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        this.showMessage("Unable to load audit history.");
        this.hideChangeSummary();
        return;
      }

      this.historyData = await response.json();
      this.renderPanel();
      this.renderChangeSummary();
      this.attachCertificateToggles();
      this.attachChangeSummaryToggle();
      this.attachQuestionHistoryIcons();
      this.attachStaticFieldHistoryIcons();
    } catch (error) {
      console.error("Failed to load audit history:", error);
      this.showMessage("Unable to load audit history.");
      this.hideChangeSummary();
    }
  }

  shouldShowPanel() {
    return shouldShowAuditHistory(this.stackStatus);
  }

  hidePanel() {
    if (this.auditHistoryTarget) {
      this.auditHistoryTarget.classList.add("hidden");
    }
  }

  hideChangeSummary() {
    if (this.changeSummaryTarget) {
      this.changeSummaryTarget.classList.add("hidden");
      this.changeSummaryTarget.innerHTML = "";
    }
  }

  showMessage(message) {
    if (!this.auditHistoryTarget) return;

    this.auditHistoryTarget.classList.remove("hidden");
    this.auditHistoryTarget.innerHTML = `<p class="text-sm text-gray-500 italic">${message}</p>`;
  }

  renderPanel() {
    if (!this.auditHistoryTarget) return;

    const versions = resolveAuditHistoryVersions(this.historyData);
    const message = this.historyData?.message;

    if (!versions.length && !message) {
      this.hidePanel();
      return;
    }

    this.auditHistoryTarget.classList.remove("hidden");
    this.auditHistoryTarget.innerHTML = this.buildPanelHtml();
  }

  renderChangeSummary() {
    if (!this.changeSummaryTarget) return;

    if (!hasEditHistory(this.historyData)) {
      this.hideChangeSummary();
      return;
    }

    const summary = this.historyData?.change_summary;
    const revisions = resolveChangeSummaryRevisions(summary);

    if (!summary?.has_changes || !revisions.length) {
      this.hideChangeSummary();
      return;
    }

    this.changeSummaryTarget.classList.remove("hidden");
    this.changeSummaryTarget.innerHTML = this.buildChangeSummaryHtml(summary);
  }

  buildChangeSummaryHtml(summary) {
    const contentHtml = buildChangeSummaryContentHtml(summary, {
      print: false,
    });

    return `
      <div class="mb-2 card border mt-4 change-summary-panel">
        <div class="card-header bg-info flex items-center justify-between">
          <div>
            <span class="mb-0 card-title text-light font-semibold">Change Summary (Revision History)</span>
            <div class="text-xs text-white opacity-90 mt-0.5">Below is the complete history of changes made to this form.</div>
          </div>
          <button type="button" class="change-summary-toggle inline-flex items-center text-white text-sm hover:opacity-90 focus:outline-none" aria-expanded="true">
            <span class="change-summary-toggle-label mr-1">Hide Change Summary</span>
            <svg class="change-summary-toggle-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="18 15 12 9 6 15"></polyline>
            </svg>
          </button>
        </div>
        <div class="change-summary-body card-body py-3 px-3 bg-gray-50">
          ${contentHtml}
          <div class="mt-3 text-xs text-gray-500 flex items-start gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mt-0.5 flex-shrink-0" aria-hidden="true">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span>All changes are captured as part of the audit trail in compliance with 21 CFR Part 11.</span>
          </div>
        </div>
      </div>
    `;
  }

  attachChangeSummaryToggle() {
    if (!this.changeSummaryTarget) return;

    const button = this.changeSummaryTarget.querySelector(
      ".change-summary-toggle",
    );
    const body = this.changeSummaryTarget.querySelector(".change-summary-body");
    const label = this.changeSummaryTarget.querySelector(
      ".change-summary-toggle-label",
    );
    const icon = this.changeSummaryTarget.querySelector(
      ".change-summary-toggle-icon",
    );
    if (!button || !body) return;

    button.addEventListener("click", (event) => {
      event.preventDefault();
      const isHidden = body.classList.toggle("hidden");
      button.setAttribute("aria-expanded", isHidden ? "false" : "true");
      if (label)
        label.textContent = isHidden
          ? "Show Change Summary"
          : "Hide Change Summary";
      if (icon)
        icon.style.transform = isHidden ? "rotate(180deg)" : "rotate(0deg)";
    });
  }

  buildPanelHtml() {
    const versions = resolveAuditHistoryVersions(this.historyData);
    const message = this.historyData?.message;
    const versionsHtml = versions
      .map((version) => this.buildVersionHtml(version))
      .join("");
    const noticeHtml = message
      ? `<div class="mb-3 px-3 py-2 rounded text-sm" style="background: #fffbeb; border: 1px solid #fde68a; color: #92400e;">${escapeHtml(message)}</div>`
      : "";
    const emptyHtml = !versions.length
      ? `<p class="text-sm text-gray-500 italic mb-0">No audit history versions are available for this form.</p>`
      : "";

    return `
      <div class="mb-2 card border mt-4">
        <div class="card-header bg-info">
          <span class="mb-0 card-title text-light font-semibold">Audit History</span>
        </div>
        <div class="card-body py-3 px-3 bg-gray-50">
          ${noticeHtml}
          <div>
            ${versionsHtml || emptyHtml}
          </div>
        </div>
      </div>
    `;
  }

  buildVersionHtml(version) {
    return buildAuditHistoryVersionHtml(version);
  }

  attachCertificateToggles() {
    if (!this.auditHistoryTarget) return;

    this.auditHistoryTarget
      .querySelectorAll(".audit-history-unique-id-toggle")
      .forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          const panel = button.parentElement?.querySelector(
            ".audit-history-certificate-panel",
          );
          if (!panel) return;

          const isHidden = panel.classList.contains("hidden");
          panel.classList.toggle("hidden", !isHidden);
          button.setAttribute("aria-expanded", isHidden ? "true" : "false");
          button.title = isHidden
            ? "Hide signature certificate details"
            : "Show signature certificate details";
        });
      });
  }

  escapeHtml(value) {
    return escapeHtml(value);
  }

  attachQuestionHistoryIcons() {
    if (!this.historyData?.question_histories) return;

    Object.entries(this.historyData.question_histories).forEach(
      ([questionKey, history]) => {
        if (this.form?.getComponent(questionKey)) {
          this.addHistoryIconToQuestion(questionKey, history);
        }
      },
    );
  }

  attachStaticFieldHistoryIcons() {
    if (!this.historyData?.question_histories) return;

    Object.entries(this.historyData.question_histories).forEach(
      ([questionKey, history]) => {
        if (this.form?.getComponent(questionKey)) return;

        document
          .querySelectorAll(`[data-form-audit-field="${questionKey}"]`)
          .forEach((labelElement) => {
            this.addHistoryIconToLabel(labelElement, history);
          });
      },
    );
  }

  addHistoryIconToQuestion(questionKey, history) {
    const component = this.form.getComponent(questionKey);
    if (!component?.element) return;

    const injectIcon = () => {
      const labelElement =
        component.element.querySelector(".control-label") ||
        component.element.querySelector(".col-form-label") ||
        component.element.querySelector("label") ||
        component.element.querySelector(".form-label");

      if (!labelElement) return;

      this.addHistoryIconToLabel(labelElement, history);
    };

    injectIcon();

    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => injectIcon());
    });

    observer.observe(component.element, { childList: true, subtree: true });
  }

  addHistoryIconToLabel(labelElement, history) {
    if (!labelElement || labelElement.querySelector(".form-audit-history-icon"))
      return;

    let container = labelElement.querySelector(".form-audit-icon-container");
    if (!container) {
      container = document.createElement("span");
      container.classList.add("form-audit-icon-container");
      container.style.display = "inline-flex";
      container.style.alignItems = "center";
      container.style.gap = "5px";

      while (labelElement.firstChild) {
        container.appendChild(labelElement.firstChild);
      }

      labelElement.appendChild(container);
    }

    const icon = document.createElement("span");
    icon.classList.add("has-tooltip", "form-audit-history-icon");
    icon.style.display = "inline-flex";
    icon.style.alignItems = "center";
    icon.style.justifyContent = "center";
    icon.style.cursor = "pointer";
    icon.style.marginLeft = "4px";
    icon.style.verticalAlign = "middle";
    icon.setAttribute("title", "View change history");
    icon.innerHTML = `
      <span style="display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 9999px; background: #ecfdf5; border: 1px solid #86efac;">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#15803d" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
          <path d="M3 3v5h5"/>
          <path d="M12 7v5l3 1.5"/>
        </svg>
      </span>
      <span class="tooltip bottom-2 bg-white text-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 text-xs min-w-max max-w-sm">
        ${this.formatQuestionHistoryTooltip(history)}
      </span>
    `;

    container.appendChild(icon);
  }

  formatQuestionHistoryTooltip(history) {
    const label = history.label || "Question";
    const entries = history.entries || [];

    if (!entries.length) {
      return `<div class="text-gray-500">${label}: No history found</div>`;
    }

    // Newest / current change first
    const displayEntries = [...entries].reverse();

    const chain = displayEntries
      .map((entry, index) => {
        const isCurrent = entry.current || index === 0;
        const timestamp = this.formatAuditTimestamp(entry.timestamp);
        const suffix = isCurrent ? " (current)" : "";

        return `
        <div class="${isCurrent ? "bg-green-100 text-green-800 font-bold" : "bg-gray-100 text-gray-800"} px-2 py-1 rounded border border-gray-200 text-sm">
          <span class="italic">${entry.display_value || entry.value || "—"}${suffix}</span>
          <span class="block text-[11px] mt-1">${entry.user_name} · ${timestamp}</span>
        </div>
      `;
      })
      .join("");

    const summary = displayEntries
      .map((entry) => this.plainText(entry.display_value || entry.value || "—"))
      .join(" ← ");

    return `
      <div class="flex flex-col gap-2">
        <div class="font-semibold text-gray-900">${label}</div>
        <div class="text-gray-600">${summary}</div>
        <div class="flex flex-col gap-1">${chain}</div>
      </div>
    `;
  }

  plainText(value) {
    if (!value) return "—";

    const element = document.createElement("div");
    element.innerHTML = value;
    return element.textContent || element.innerText || value;
  }

  formatAuditTimestamp(isoString) {
    return formatAuditTimestamp(isoString);
  }
}
